import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Form, Button, Table, Alert, Spinner, Badge,
    Modal, InputGroup
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";


export default function NouveauDevis() {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [clients, setClients] = useState([]);
    const [produits, setProduits] = useState([]);
    const [showProduitModal, setShowProduitModal] = useState(false);
    const [searchProduit, setSearchProduit] = useState("");
    
    const [taxes, setTaxes] = useState({
        tva_taux: 18,
        css_taux: 1,
        tva_active: true,
        css_active: true
    });

    const [devis, setDevis] = useState({
        id_client: '',
        date_proforma: new Date().toISOString().split('T')[0],
        date_validite: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        remise_globale: 0,
        notes: '',
        statut: 'brouillon',
        lignes: []
    });

    useEffect(() => {
        fetchClients();
        fetchProduits();
        fetchTaxes();
    }, []);

    const fetchTaxes = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/parametres.php?action=taxes`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                if (result.data) {
                    setTaxes(result.data);
                }
            }
        } catch (err) {
            console.error("Erreur chargement taxes", err);
        }
    };

    const fetchClients = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                setClients(result.data || []);
            }
        } catch (err) {
            console.error("Erreur chargement clients", err);
        }
    };

    const fetchProduits = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/produits.php?include_stock=true`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
                const result = await response.json();
                const produitsAvecStock = (result.data || []).map(p => ({
                    ...p,
                    stock_reel: p.stock_reel || 0,
                    seuil_alerte: p.seuil_alerte || 5,
                    prix_vente_ht: parseFloat(p.prix_vente_ht) || 0
                }));
                setProduits(produitsAvecStock);
            }
        } catch (err) {
            console.error("Erreur chargement produits", err);
        }
    };

    const ajouterLigne = (produit) => {
        const nouvelleLigne = {
            id_produit: produit.id_produit,
            reference: produit.reference,
            designation: produit.designation,
            quantite: 1,
            prix_unitaire_ht: produit.prix_vente_ht,
            tva_taux: taxes.tva_active ? taxes.tva_taux : 0,
            css_taux: taxes.css_active ? taxes.css_taux : 0,
            remise: 0
        };
        setDevis({
            ...devis,
            lignes: [...devis.lignes, nouvelleLigne]
        });
        setShowProduitModal(false);
        setSearchProduit('');
    };

    const supprimerLigne = (index) => {
        const nouvellesLignes = devis.lignes.filter((_, i) => i !== index);
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const updateLigne = (index, champ, valeur) => {
        const nouvellesLignes = [...devis.lignes];
        nouvellesLignes[index][champ] = valeur;
        setDevis({ ...devis, lignes: nouvellesLignes });
    };

    const calculerTotaux = () => {
        let total_ht = 0;
        let total_tva = 0;
        let total_css = 0;

        devis.lignes.forEach(ligne => {
            const montant_ligne_ht = ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0) / 100);
            total_ht += montant_ligne_ht;
            
            if (taxes.tva_active) {
                total_tva += montant_ligne_ht * (ligne.tva_taux || taxes.tva_taux) / 100;
            }
            if (taxes.css_active) {
                total_css += montant_ligne_ht * (ligne.css_taux || taxes.css_taux) / 100;
            }
        });

        const total_ttc = total_ht + total_tva + total_css;
        const net_a_payer = total_ttc - (devis.remise_globale || 0);

        return { total_ht, total_tva, total_css, total_ttc, net_a_payer };
    };

    const totaux = calculerTotaux();

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!devis.id_client) {
            setError("Veuillez sélectionner un client");
            return;
        }
        
        if (devis.lignes.length === 0) {
            setError("Veuillez ajouter au moins un produit");
            return;
        }

        setSaving(true);
        setError("");

        try {
            const userStr = localStorage.getItem("user");
            const user = userStr ? JSON.parse(userStr) : null;
            
            if (!user || !user.id_utilisateur) {
                setError("Utilisateur non connecté");
                return;
            }

            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/devis.php`,
                {
                    method: 'POST',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        action: 'create',
                        ...devis,
                        id_utilisateur: user.id_utilisateur
                    })
                }
            );

            const result = await response.json();

            if (result.success) {
                setSuccess("Devis créé avec succès !");
                setTimeout(() => {
                    navigate('/ventes/devisListe');
                }, 2000);
            } else {
                setError(result.message || "Erreur lors de la création");
            }
        } catch (err) {
            setError("Erreur de connexion au serveur");
        } finally {
            setSaving(false);
        }
    };

    const produitsFiltres = produits.filter(p => 
        p.designation?.toLowerCase().includes(searchProduit.toLowerCase()) ||
        p.reference?.toLowerCase().includes(searchProduit.toLowerCase())
    );

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* En-tête simplifié */}
                    <div className="d-md-flex align-items-center justify-content-between mb-4">
                        <div>
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                    <i className="ri-file-copy-line fs-20 text-primary" style={{ color: '#21ACCD' }}></i>
                                </div>
                                <div>
                                    <h4 className="main-title mb-0 fw-bold">Nouveau devis</h4>
                                    <p className="text-muted small mb-0">Créez un devis ou une proposition commerciale</p>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 mt-3 mt-md-0">
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => navigate('/ventes/devisListe')}
                                className="rounded-pill px-3 d-flex align-items-center gap-1"
                                size="sm"
                            >
                                <i className="ri-arrow-left-line"></i>
                                Retour
                            </Button>
                        </div>
                    </div>

                    {/* Alertes */}
                    {error && (
                        <Alert variant="danger" onClose={() => setError("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-error-warning-line me-2 fs-5"></i>
                            {error}
                        </Alert>
                    )}
                    
                    {success && (
                        <Alert variant="success" onClose={() => setSuccess("")} dismissible className="mb-4 alert-modern">
                            <i className="ri-checkbox-circle-line me-2 fs-5"></i>
                            {success}
                        </Alert>
                    )}

                    <Form onSubmit={handleSubmit}>
                        <Row>
                            {/* Informations générales */}
                            <Col lg={8}>
                                <Card className="border-0 shadow-sm rounded-3 mb-4">
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-information-line me-2"></i>
                                            Informations générales
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <Row className="g-3">
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Client <span className="text-danger">*</span></Form.Label>
                                                    <Form.Select
                                                        value={devis.id_client}
                                                        onChange={(e) => setDevis({...devis, id_client: e.target.value})}
                                                        required
                                                        className="border-2 rounded-3"
                                                    >
                                                        <option value="">Sélectionner un client</option>
                                                        {clients.map(client => (
                                                            <option key={client.id_client} value={client.id_client}>
                                                                {client.raison_sociale} ({client.code_client})
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date devis</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_proforma}
                                                        onChange={(e) => setDevis({...devis, date_proforma: e.target.value})}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={3}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Date validité</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={devis.date_validite}
                                                        onChange={(e) => setDevis({...devis, date_validite: e.target.value})}
                                                        required
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label className="fw-semibold">Notes</Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        value={devis.notes}
                                                        onChange={(e) => setDevis({...devis, notes: e.target.value})}
                                                        placeholder="Conditions particulières, délais, etc..."
                                                        className="border-2 rounded-3"
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Lignes de devis */}
                                <Card className="border-0 shadow-sm rounded-3">
                                    <Card.Header className="py-3 d-flex justify-content-between align-items-center" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-shopping-cart-line me-2"></i>
                                            Produits / Services
                                        </h5>
                                        <Button 
                                            variant="light" 
                                            size="sm"
                                            onClick={() => setShowProduitModal(true)}
                                            className="rounded-pill"
                                        >
                                            <i className="ri-add-line me-1"></i>
                                            Ajouter
                                        </Button>
                                    </Card.Header>
                                    <Card.Body className="p-0">
                                        {devis.lignes.length > 0 ? (
                                            <div className="table-responsive">
                                                <Table className="align-middle mb-0 products-table">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th className="ps-4">Référence</th>
                                                            <th>Désignation</th>
                                                            <th className="text-center">Qté</th>
                                                            <th className="text-end">Prix HT</th>
                                                            <th className="text-center">Remise</th>
                                                            <th className="text-end">Total HT</th>
                                                            <th className="text-center">TVA</th>
                                                            <th className="text-center pe-4"></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {devis.lignes.map((ligne, index) => (
                                                            <tr key={index} className="product-row">
                                                                <td className="ps-4">
                                                                    <span className="reference-badge">
                                                                        <i className="ri-barcode-line me-1 fs-10"></i>
                                                                        {ligne.reference}
                                                                    </span>
                                                                </td>
                                                                <td className="fw-semibold">{ligne.designation}</td>
                                                                <td className="text-center">
                                                                    <Form.Control
                                                                        type="number"
                                                                        min="1"
                                                                        value={ligne.quantite}
                                                                        onChange={(e) => updateLigne(index, 'quantite', parseInt(e.target.value) || 1)}
                                                                        size="sm"
                                                                        className="text-center border-2 rounded-3"
                                                                        style={{ width: '80px', margin: '0 auto' }}
                                                                    />
                                                                </td>
                                                                <td className="text-end">
                                                                    <Form.Control
                                                                        type="number"
                                                                        step="0.01"
                                                                        value={ligne.prix_unitaire_ht}
                                                                        onChange={(e) => updateLigne(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                                                                        size="sm"
                                                                        className="text-end border-2 rounded-3"
                                                                        style={{ width: '100px' }}
                                                                    />
                                                               </td>
                                                                <td className="text-center" style={{ width: '90px' }}>
                                                                    <InputGroup size="sm">
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="0"
                                                                            max="100"
                                                                            value={ligne.remise || 0}
                                                                            onChange={(e) => updateLigne(index, 'remise', parseFloat(e.target.value) || 0)}
                                                                            className="text-center border-2 rounded-3"
                                                                        />
                                                                        <InputGroup.Text className="border-2">%</InputGroup.Text>
                                                                    </InputGroup>
                                                                 </td>
                                                                <td className="text-end fw-bold text-primary">
                                                                    {formatCurrency(ligne.quantite * ligne.prix_unitaire_ht * (1 - (ligne.remise || 0)/100))}
                                                                </td>
                                                                <td className="text-center">
                                                                    <span className="tva-badge">
                                                                        {taxes.tva_active ? (ligne.tva_taux || taxes.tva_taux) : 0}%
                                                                    
                                                                 </span></td>
                                                                <td className="text-center pe-4">
                                                                    <Button 
                                                                        variant="link" 
                                                                        className="text-danger p-0"
                                                                        onClick={() => supprimerLigne(index)}
                                                                    >
                                                                        <i className="ri-delete-bin-line fs-16"></i>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-5">
                                                <i className="ri-shopping-cart-line fs-48 text-muted mb-3 d-block"></i>
                                                <p className="text-muted mb-3">Aucun produit ajouté</p>
                                                <Button 
                                                    variant="outline-primary"
                                                    onClick={() => setShowProduitModal(true)}
                                                    className="rounded-pill px-4"
                                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                                >
                                                    <i className="ri-add-line me-2"></i>
                                                    Ajouter un produit
                                                </Button>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Récapitulatif */}
                            <Col lg={4}>
                                <Card className="border-0 shadow-sm rounded-3 sticky-top" style={{ top: '20px' }}>
                                    <Card.Header className="py-3" style={{ background: '#21ACCD', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                                        <h5 className="mb-0 text-white">
                                            <i className="ri-calculator-line me-2"></i>
                                            Récapitulatif
                                        </h5>
                                    </Card.Header>
                                    <Card.Body className="p-4">
                                        <div className="recap-line d-flex justify-content-between mb-2">
                                            <span className="text-muted">Total HT</span>
                                            <span className="fw-bold">{formatCurrency(totaux.total_ht)}</span>
                                        </div>
                                        {taxes.tva_active && totaux.total_tva > 0 && (
                                            <div className="recap-line d-flex justify-content-between mb-2">
                                                <span className="text-muted">TVA ({taxes.tva_taux}%)</span>
                                                <span className="text-warning fw-semibold">{formatCurrency(totaux.total_tva)}</span>
                                            </div>
                                        )}
                                        {taxes.css_active && totaux.total_css > 0 && (
                                            <div className="recap-line d-flex justify-content-between mb-2">
                                                <span className="text-muted">CSS ({taxes.css_taux}%)</span>
                                                <span className="text-info fw-semibold">{formatCurrency(totaux.total_css)}</span>
                                            </div>
                                        )}
                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-3">
                                            <span className="fw-bold">Total TTC</span>
                                            <span className="fw-bold text-primary fs-5">{formatCurrency(totaux.total_ttc)}</span>
                                        </div>

                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-semibold">Remise globale (FCFA)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                min="0"
                                                value={devis.remise_globale}
                                                onChange={(e) => setDevis({...devis, remise_globale: parseFloat(e.target.value) || 0})}
                                                className="border-2 rounded-3"
                                            />
                                        </Form.Group>

                                        <hr />
                                        <div className="recap-line d-flex justify-content-between mb-4">
                                            <span className="h6">Net à payer</span>
                                            <span className="h5 text-success fw-bold">{formatCurrency(totaux.net_a_payer)}</span>
                                        </div>

                                        <div className="d-grid gap-2">
                                            <Button 
                                                type="submit" 
                                                variant="primary" 
                                                size="lg"
                                                disabled={saving}
                                                className="rounded-pill py-2"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2" />
                                                        Création...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Créer le devis
                                                    </>
                                                )}
                                            </Button>
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={() => navigate('/ventes/devisListe')}
                                                className="rounded-pill py-2"
                                            >
                                                Annuler
                                            </Button>
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    </Form>
                </div>
                <Footer />
            </div>

            {/* Modal de sélection des produits modernisé */}
            <Modal show={showProduitModal} onHide={() => setShowProduitModal(false)} size="lg" centered className="product-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4" style={{ background: '#21ACCD' }}>
                    <Modal.Title className="text-white">
                        <i className="ri-shopping-cart-line me-2"></i>
                        Sélectionner un produit
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    <InputGroup className="mb-3">
                        <InputGroup.Text className="bg-light border-2 rounded-pill">
                            <i className="ri-search-line"></i>
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Rechercher un produit..."
                            value={searchProduit}
                            onChange={(e) => setSearchProduit(e.target.value)}
                            className="border-2 rounded-pill"
                        />
                    </InputGroup>
                    <div className="table-responsive">
                        <Table hover className="align-middle mb-0">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">Référence</th>
                                    <th>Désignation</th>
                                    <th className="text-end">Prix HT</th>
                                    <th className="text-center">Stock</th>
                                    <th className="pe-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {produitsFiltres.map(produit => (
                                    <tr key={produit.id_produit}>
                                        <td className="ps-4"><span className="reference-badge">{produit.reference}</span></td>
                                        <td className="fw-semibold">{produit.designation}</td>
                                        <td className="text-end">{formatCurrency(produit.prix_vente_ht)}</td>
                                        <td className="text-center">
                                            <span className={produit.stock_reel < produit.seuil_alerte ? 'text-danger fw-bold' : 'text-muted'}>
                                                {produit.stock_reel || 0}
                                            </span>
                                            {produit.stock_reel <= 0 && (
                                                <i className="ri-close-circle-line text-danger ms-1" title="Rupture de stock"></i>
                                            )}
                                            {produit.stock_reel > 0 && produit.stock_reel < produit.seuil_alerte && (
                                                <i className="ri-alert-line text-warning ms-1" title="Stock critique"></i>
                                            )}
                                       </td>
                                        <td className="pe-4">
                                            <Button 
                                                size="sm" 
                                                variant="primary"
                                                onClick={() => ajouterLigne(produit)}
                                                disabled={produit.stock_reel <= 0}
                                                className="rounded-circle"
                                                style={{ 
                                                    background: produit.stock_reel <= 0 ? '#94a3b8' : '#21ACCD', 
                                                    border: 'none', 
                                                    width: '32px', 
                                                    height: '32px' 
                                                }}
                                            >
                                                <i className="ri-add-line"></i>
                                            </Button>
                                            {produit.stock_reel <= 0 && (
                                                <small className="text-danger d-block">Rupture</small>
                                            )}
                                      </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="outline-secondary" onClick={() => setShowProduitModal(false)} className="rounded-pill px-4">
                        Fermer
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                
                .reference-badge {
                    background: #f1f5f9;
                    padding: 0.2rem 0.6rem;
                    border-radius: 6px;
                    font-size: 0.7rem;
                    font-family: monospace;
                    display: inline-block;
                }
                
                .tva-badge {
                    background: #fef3c7;
                    padding: 0.2rem 0.6rem;
                    border-radius: 20px;
                    font-size: 0.7rem;
                    font-weight: 600;
                    color: #d97706;
                }
                
                .recap-line {
                    padding: 0.5rem 0;
                }
                
                .products-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .products-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .product-row {
                    transition: all 0.2s ease;
                }
                
                .product-row:hover {
                    background: #f8fafc;
                }
                
                .product-modal .modal-content {
                    border-radius: 24px;
                    overflow: hidden;
                }
                
                .alert-modern {
                    border: none;
                    border-radius: 12px;
                    padding: 1rem 1.5rem;
                    box-shadow: 0 4px 8px rgba(0,0,0,0.05);
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-16 {
                    font-size: 1rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-48 {
                    font-size: 3rem;
                }
            `}</style>
        </>
    );
}